import React, { useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/core";
import { Icon } from "assets/Icon";
// `newui/components/Button` (not the legacy `components/Button`, whose prop type
// demands a raft of unrelated required props) — same button MyTemplatesButton uses.
import { Button } from "newui/components/Button";
import { MyTemplatesButton } from "pages/RFQ/Templates/_components/MyTemplatesButton";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { CreateRFxSlider } from "newui/features/Rfx/Creation/CreateRFxSlider";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useRfxNavigate } from "app/useRfxNavigate";

export interface TendersQuotesHeaderActionsProps {
	companyId: string | number;
}

/**
 * "My Templates" + "Create RFx" — the two scope-level actions that used to live
 * in legacy `RFQ.js`'s `TabHeader` (@ origin/staging:196-230) and were dropped
 * in the PRO-637 restructure.
 *
 * Rendered from the page header, so they show on EVERY Tenders & Quotes tab and
 * regardless of whether the list has rows: creating an RFx is most useful
 * precisely when there are none. `Create RFx` stays inside `ReadOnlyGuard` — a
 * switched (executive read-only) user must not be able to start one.
 */
export const TendersQuotesHeaderActions = ({
	companyId,
}: TendersQuotesHeaderActionsProps) => {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const { clientPaths } = useNavigationPaths();
	const navigate = useRfxNavigate();

	const handleCreateTemplate = () => {
		navigate(clientPaths.rfx.createTemplate);
	};

	const handleCreateClick = () => {
		setIsCreateOpen(true);
		onOpen();
	};

	const handleCreateClose = () => {
		setIsCreateOpen(false);
		onClose();
	};

	return (
		<>
			<div className="flex items-center gap-6">
				<MyTemplatesButton
					handleCreateTemplate={handleCreateTemplate}
					companyId={companyId}
				/>
				<ReadOnlyGuard>
					<Button
						style={{ height: "50px" }}
						onClick={handleCreateClick}
						data-testid="create-rfq-button"
					>
						<Flex className="items-center gap-[6px]">
							<div className="flex h-5 w-5 items-center justify-center">
								<Icon
									icon="plus"
									style={{
										width: "12px",
										height: "12px",
										color: "#fff",
										fontWeight: "bold",
									}}
								/>
							</div>
							<div className="font-roboto text-base font-semibold capitalize">
								Create RFx
							</div>
						</Flex>
					</Button>
				</ReadOnlyGuard>
			</div>

			{isCreateOpen && (
				<CreateRFxSlider isOpen={isOpen} onClose={handleCreateClose} />
			)}
		</>
	);
};
